import { Fragment, useEffect, useState } from 'react'
import { MetadataTabWidget } from '../MetadataTabWidget.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import type { IOrder, ISamplePool } from '../../../../Model/index.js'
import { isSampleIncomplete } from '../../../../Model/index.js'
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, NoSymbolIcon, PlusIcon } from '@heroicons/react/24/outline/index.js'
import { SamplePoolWidget } from '../SamplePoolWidget.js'
import { poolDisplayName } from '../PoolName.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import { isOrgMemberOfOrder } from '../Actions/OrderActionsChecks.js'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { useDispatch } from 'react-redux'
import { useInteraction } from '../../../../Framework/View/Hooks/useInteraction.js'
import type { OrderState } from '../../../../Util/OrderState.js'
import { useUppy } from '../../../../Provider/UppyProvider.js'
import { useGetSampleMetaDataSelector } from '../../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import type { Subject } from 'rxjs'
import { trackOrderAction, trackOrderSection, trackOrderTabSwitch } from '../../../../Services/Telemetry/index.js'

interface OrderDetailTabSectionProps {
  order: IOrder
  samplePools: ISamplePool[] | 'LOADING'
  orderState: OrderState
  message$: Subject<{
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
  }>
}

export const OrderDetailTabSection = (props: OrderDetailTabSectionProps) => {
  const user = useGetAuthenticatedUser()

  /*
   * Metadata is editable for longer, and by more people, than the order is. The server allows it for
   * any org on the order for the order's whole life — `SampleMetadataController.updateBatch` checks
   * only `existsByIdAndOrganization`, with no submission or DONE condition — which is what the CSV
   * importer and the edit grid have always relied on. The tables were gating it on order editability
   * instead, so their metadata controls vanished at submission while the other two paths kept working.
   */
  const metadataEditable = isOrgMemberOfOrder(user, props.order)
  const dispatch = useDispatch()
  const [tabsCollapsed, setTabsCollapsed] = useState<boolean>(false)
  const addNewSamplePool$ = useInteraction<void>()
  const [selectedIndex, setSelectedDatasetIndex]: [number, (ds: number) => void] = useState(0)
  const [initialPoolHandled, setInitialPoolHandled] = useState(false)
  const { meta, setMeta } = useUppy()
  const metaData = useGetSampleMetaDataSelector()
  const [mobilePoolsMenuOpen, setMobilePoolsMenuOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!initialPoolHandled) {
      if (props.samplePools !== 'LOADING') {
        setSelectedDatasetIndex((props.samplePools.length ?? 0) > 0 ? 1 : 0)
        setInitialPoolHandled(true)
      }
    }
  }, [props.samplePools])

  useEffect(() => {
    /* update global defaults */
    if (props.samplePools === 'LOADING' || !props.samplePools?.length || !props.order?.id) return
    // selectedIndex 0 is the Metadata tab; pool `i` lives at tab `i + 1` (see the tab rendering
    // below). So the selected pool is samplePools[selectedIndex - 1]. On the Metadata tab this is
    // samplePools[-1] === undefined and we bail out (no upload target), which is intended.
    const selectedPool = props.samplePools[selectedIndex - 1]
    if (!selectedPool) return

    setMeta({
      orderId: props.order.id,
      poolId: selectedPool.id,
      itemType: meta?.itemType ?? 'SAMPLE',
      isTrimmed: props.order.sampleConfiguration === 'TRIMMED_SAMPLES' || props.order.sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES',
      isMerged: props.order.sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES' || props.order.sampleConfiguration === 'MERGED_SAMPLES'
    })
  }, [props.order.id, selectedIndex, props.samplePools])

  /*
   * A finished order opens collapsed — but only as a starting position. Keying this on the pools (or
   * on the whole order object) re-ran it whenever their contents changed, so any edit inside a done
   * order slammed the tabs shut again underneath the user: clearing one metadata field closed the pool
   * they were working in.
   */
  useEffect(() => {
    setTabsCollapsed(!!props.order.orderDone)
  }, [props.order.id, props.order.orderDone])

  // ---------------------------------------------------------------------------
  // Tracking: tab switches + expand/collapse
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const sectionName = selectedIndex === 0 ? 'metadata' : `pool-${selectedIndex - 1}`
    trackOrderTabSwitch(sectionName)
  }, [selectedIndex])

  const handleToggleTabs = () => {
    const action: 'expand' | 'collapse' = !tabsCollapsed ? 'expand' : 'collapse'
    trackOrderSection('all', action)
    setTabsCollapsed(!tabsCollapsed)
  }

  const addNewSamplePool = () => {
    addNewSamplePool$.next()
  }

  useEffect(() => {
    const sub = addNewSamplePool$.subscribe(() => {
      dispatch(Actions[orderSlice.name].createSamplepoolRequest({ orderId: props.order.id }))
      if (selectedIndex === 0) {
        setSelectedDatasetIndex(1)
      }
    })

    return () => sub.unsubscribe()
  }, [dispatch, addNewSamplePool$])

  if (props.samplePools === 'LOADING') {
    return (
      <>
        <div className="border-t-2 mt-2 pt-2 h-12 flex items-center justify-center gap-2">
          <span className={'text-gray text-sm'}>Loading Pools...</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6 animate-spin"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={'hidden sm:max-md:flex w-full my-2 gap-2'}>
        <button className={'btn btn-lg grow'} onClick={handleToggleTabs}>
          {tabsCollapsed ? (
            <>
              Expand Pools
              <ChevronDownIcon className="w-4 h-4 inline-block" />
            </>
          ) : (
            <>
              Collapse Pools
              <ChevronUpIcon className="w-4 h-4 inline-block" />
            </>
          )}
        </button>

        <button className={'btn btn-lg grow'} onClick={() => setMobilePoolsMenuOpen((prev) => !prev)}>
          Pools
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      {mobilePoolsMenuOpen && (
        <div className={'w-full hidden sm:max-md:flex flex-col gap-y-2'}>
          <button className={'btn btn-lg grow '} disabled={selectedIndex == 0} onClick={() => setSelectedDatasetIndex(0)}>
            Metadata
          </button>
          {props.samplePools.map((d, i) => (
            <button key={i} disabled={selectedIndex == i + 1} onClick={() => setSelectedDatasetIndex(i + 1)} className={'btn btn-lg grow'}>
              {poolDisplayName(d, i)}
            </button>
          ))}
          <button className={'btn btn-lg grow text-pretty'} onClick={() => addNewSamplePool()}>
            <PlusIcon className="w-5 h-5 inline-block " /> new Samplepool...
          </button>
        </div>
      )}

      {user && (
        <div className="border-t-2 mt-2 pt-2">
          <div className="w-full overflow-x-auto whitespace-nowrap scrollbar-thin">
            <div role="tablist" className="tabs tabs-lifted w-full">
              <Fragment>
                <a
                  role="tab"
                  aria-selected={selectedIndex === 0}
                  className={`tab w-32 sm:max-md:hidden ${
                    selectedIndex === 0
                      ? 'tab-active font-bold text-primary [--tab-border-color:var(--fallback-p,oklch(var(--p)))] border-b-2'
                      : 'font-light text-base-content/60'
                  }`}
                  onClick={() => setSelectedDatasetIndex(0)}
                >
                  <span>Metadata</span>
                </a>
                {!tabsCollapsed && (
                  <div
                    role="tabpanel"
                    className="sticky start-0 w-full min-w-full max-w-[80vw] tab-content bg-base-100 border-base-300 rounded-box p-6"
                    key={0 + 'Pool'}
                  >
                    <MetadataTabWidget
                      allPools={props.samplePools}
                      /* Same rule as the pool tabs: the owning lab may edit until the order is submitted.
                         Metadata mutations (delete / unlink / rename) target the metadata id + orderId, not
                         the mock pool, so they are safe even though the rows are dummy samples. */
                      editable={!props.order.orderSubmission && user?.org?.id === props.order.lab}
                      metadataEditable={metadataEditable}
                      metadata={metaData}
                      key={0 + 'poolWidget'}
                      order={props.order}
                      orderState={props.orderState}
                    />
                  </div>
                )}
              </Fragment>
              {props.samplePools.map((d, i) => (
                <Fragment key={d.id}>
                  <a
                    role="tab"
                    aria-selected={selectedIndex === i + 1}
                    className={`tab w-32 sm:max-md:hidden ${
                      selectedIndex === i + 1
                        ? 'tab-active font-bold text-primary [--tab-border-color:var(--fallback-p,oklch(var(--p)))] border-b-2'
                        : 'font-light text-base-content/60'
                    }`}
                    key={d.id}
                    data-test-id={`select-pool-${i}`}
                    onClick={() => setSelectedDatasetIndex(i + 1)}
                  >
                    <TooltipTrigger tooltip={`Pool id: ${d.id}. Shift+Click on the tab header to copy the pool id`}>
                      <span
                        onClick={(e) => {
                          if (e.shiftKey) {
                            navigator.clipboard
                              .writeText(d.id)
                              .then(() => {
                                // Optional: notify user
                                console.log('Copied ID to clipboard:', d.id)
                              })
                              .catch((err) => {
                                console.error('Failed to copy:', err)
                              })
                            // Prevent any other click action if needed
                            e.stopPropagation()
                          }
                        }}
                        className="cursor-pointer"
                      >
                        {d.samples.find((s) => isSampleIncomplete(s)) && d.negativeControls.find((nc) => isSampleIncomplete(nc)) ? (
                          <NoSymbolIcon className="fill-yellow-500 h-3 w-3 inline-block mr-2" />
                        ) : (
                          <CheckCircleIcon className="fill-green-500 w-3 h-3 inline-block mr-2" />
                        )}
                        <span className="inline-block max-w-20 truncate align-bottom" title={poolDisplayName(d, i)}>
                          {poolDisplayName(d, i)}
                        </span>
                      </span>
                    </TooltipTrigger>
                  </a>
                  {!tabsCollapsed && (
                    <div
                      role="tabpanel"
                      className="sticky start-0 md:min-w-full w-full md:max-w-[80vw] tab-content bg-base-100 border-base-300 rounded-box p-6"
                      key={d.id + 'Pool'}
                    >
                      <SamplePoolWidget
                        message$={props.message$}
                        editable={!props.order.orderSubmission && user?.org?.id === props.order.lab}
                        renamable={metadataEditable && !!d.version}
                        index={i}
                        metadataEditable={metadataEditable}
                        samplePool={d}
                        key={d.id + 'poolWidget'}
                        order={props.order}
                        orderState={props.orderState}
                        allPools={props.samplePools === 'LOADING' ? [] : props.samplePools}
                        onPoolDeleted={() => {
                          if (i + 1 === selectedIndex) {
                            setSelectedDatasetIndex(selectedIndex - 1)
                          }
                        }}
                      />
                    </div>
                  )}
                </Fragment>
              ))}
              {!props.order.orderSubmission && user?.org?.id == props.order.lab && (
                <label role="tab" className="tab w-52 flex flex-row sm:max-md:hidden" htmlFor="uploadDataSetInput">
                  <TooltipTrigger
                    tooltip={
                      'Create a new sample pool. Sample pools group your negative controls together with the samples they should be applied to.'
                    }
                  >
                    <span
                      data-testid="add-sample-pool"
                      className="text-pretty"
                      onClick={() => {
                        trackOrderAction('add_sample_pool', props.order)
                        addNewSamplePool()
                      }}
                    >
                      <PlusIcon className="w-5 h-5 inline-block " /> new Samplepool...
                    </span>
                  </TooltipTrigger>
                </label>
              )}
              <label role="tab" className={'tab w-auto sm:max-md:hidden'}></label>
              <label role={'tab'} className="tab sm:max-md:hidden inline-flex" onClick={handleToggleTabs}>
                {tabsCollapsed ? (
                  <>
                    Expand All
                    <ChevronDownIcon className="w-4 h-4 inline-block" />
                  </>
                ) : (
                  <>
                    Collapse
                    <ChevronUpIcon className="w-4 h-4 inline-block" />
                  </>
                )}
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
